import is from '@sindresorhus/is';
import { useMemo } from 'react';

import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import {
    applySectionCommentRangesToHtml,
    resolveCommentAnchorRange,
    sectionTextFromHtml,
} from './comment-anchor-markup';
import type { CommentMarkRange } from './comment-classification';
import { isCommentsScopeCurrent, useCommentsScope } from './comments-scope-context';

const EMPTY_COMMENT_MARK_RANGES: readonly CommentMarkRange[] = [];

export const useResolvedSectionMarkedHtml = (
    sectionId: string | undefined,
    html: string | undefined,
): string | undefined => {
    const scope = useCommentsScope();
    const pendingTarget = useActiveThreadStore((state) => state.pendingTarget);
    const sectionMarkRanges = useActiveThreadStore((state) =>
        is.undefined(sectionId) || !isCommentsScopeCurrent(state.commentClassificationScope, scope)
            ? EMPTY_COMMENT_MARK_RANGES
            : (state.commentClassification?.sectionMarkRanges.find((section) => section.sectionId === sectionId)
                  ?.ranges ?? EMPTY_COMMENT_MARK_RANGES),
    );

    return useMemo(() => {
        if (is.undefined(sectionId) || is.undefined(html)) return html;

        const ranges: CommentMarkRange[] = [...sectionMarkRanges];
        if (
            !is.null(pendingTarget) &&
            pendingTarget.target.kind === 'description-section' &&
            pendingTarget.target.sectionId === sectionId &&
            !is.null(pendingTarget.anchor) &&
            !ranges.some((range) => range.commentId === pendingTarget.commentId)
        ) {
            const pendingRange = resolveCommentAnchorRange(pendingTarget.anchor, sectionTextFromHtml(html));
            if (!is.null(pendingRange)) {
                ranges.push({ ...pendingRange, commentId: pendingTarget.commentId });
            }
        }

        return applySectionCommentRangesToHtml(html, ranges);
    }, [html, pendingTarget, sectionId, sectionMarkRanges]);
};
